import { readPortalToken } from "@/domains/auth/lib/portalToken";
import { generateRequestId, resolveApiUrl } from "./apiBase";

export interface ApiRequestOptions {
  body?: unknown;
  silent?: boolean;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  status: number;
  details?: unknown;
  isAuthError: boolean;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
    this.isAuthError = status === 401 || status === 302;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isAuthError(error: unknown): boolean {
  return isApiError(error) ? error.isAuthError : error instanceof Error && error.message === "auth";
}

async function parseErrorResponse(response: Response) {
  const text = await response.text();
  if (!text) return { message: `HTTP ${response.status}`, details: undefined };

  try {
    const parsed = JSON.parse(text);
    return {
      message: parsed.error || parsed.message || `HTTP ${response.status}`,
      details: parsed,
    };
  } catch {
    return { message: text || `HTTP ${response.status}`, details: text };
  }
}

export async function apiRequest<T>(
  method: string,
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const token = readPortalToken();
  const headers: Record<string, string> = options.body ? { "Content-Type": "application/json" } : {};
  headers["X-Request-Id"] = generateRequestId();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(resolveApiUrl(path), {
    method,
    credentials: "include",
    headers,
    cache: "no-store",
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (response.status === 401 || response.status === 302) {
    throw new ApiError("auth", response.status);
  }

  if (response.status === 304) {
    return null as T;
  }

  if (!response.ok) {
    const { message, details } = await parseErrorResponse(response);
    throw new ApiError(message, response.status, details);
  }

  if (response.status === 204) {
    return null as T;
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return null as T;
  }

  return await response.json() as T;
}
